// src/modules/marketplace/components/kraken/citiesBarChart.tsx

import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { Box, useTheme } from "@mui/material"
import { Kraken } from "../../interfaces"
import { formatNumber, formatPrice } from "./utils"

interface CitiesBarChartProps {
  cities: Kraken.CityStat[]
}

const LABELS: Record<string, string> = {
  loaded: "Загружено",
  sold_kraken: "Продано (kraken)",
  sold_liga: "Продано (liga)",
  sold_other: "Продано (прочие)",
  avg_price: "Средняя цена",
}

export function CitiesBarChart({ cities }: CitiesBarChartProps) {
  const theme = useTheme()
  const gridColor =
    theme.palette.mode === "light"
      ? theme.palette.grey[300]
      : theme.palette.grey[700]

  return (
    <Box>
      <ResponsiveContainer width="100%" height={360}>
        <ComposedChart
          data={cities}
          margin={{ top: 8, right: 24, left: 12, bottom: 40 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
          <XAxis
            dataKey="city"
            tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
            angle={-30}
            textAnchor="end"
            interval={0}
          />
          <YAxis
            yAxisId="count"
            orientation="left"
            tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
            tickFormatter={(v) => formatNumber(v)}
            label={{
              value: "Количество, шт",
              angle: -90,
              position: "insideLeft",
              style: {
                fontSize: 11,
                fill: theme.palette.text.secondary,
                textAnchor: "middle",
              },
            }}
          />
          <YAxis
            yAxisId="price"
            orientation="right"
            tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
            label={{
              value: "Средняя цена, ₽",
              angle: 90,
              position: "insideRight",
              style: {
                fontSize: 11,
                fill: theme.palette.text.secondary,
                textAnchor: "middle",
              },
            }}
          />
          <Tooltip
            formatter={(
              value: number | undefined,
              name: string | undefined,
            ) => [
              name === "avg_price" ? formatPrice(value) : formatNumber(value),
              (name && LABELS[name]) ?? name,
            ]}
            contentStyle={{
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${gridColor}`,
              borderRadius: 4,
              fontSize: 12,
            }}
          />
          <Legend
            formatter={(value) => LABELS[value] ?? value}
            wrapperStyle={{ fontSize: 12 }}
          />
          <Bar
            yAxisId="count"
            dataKey="loaded"
            stackId="loaded"
            fill={theme.palette.info.main}
            opacity={0.7}
            radius={[2, 2, 0, 0]}
          />
          <Bar
            yAxisId="count"
            dataKey="sold_kraken"
            stackId="sold"
            fill={theme.palette.success.main}
          />
          <Bar
            yAxisId="count"
            dataKey="sold_liga"
            stackId="sold"
            fill="#f57c00"
          />
          <Bar
            yAxisId="count"
            dataKey="sold_other"
            stackId="sold"
            fill={theme.palette.grey[500]}
            radius={[2, 2, 0, 0]}
          />
          <Line
            yAxisId="price"
            dataKey="avg_price"
            stroke={theme.palette.secondary.main}
            strokeWidth={2}
            dot={false}
            type="monotone"
            connectNulls
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  )
}
